import { Injectable, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { StorageService } from './storage/storage.service';
import { PlatformService } from './platform.service';
import { CrossTabCommunicationService } from './cross-tab-communication.service';
import { firstValueFrom, catchError } from 'rxjs';
import { of } from 'rxjs';
import {
  SignInUserData,
  UserGroup,
  UserStatus,
} from '@features/authentication/models/authentication.model';
import { AuthenticationApiService } from '@features/authentication/services/authentication-api.service';
import {
  AllowedOrganization,
  OrganizationSessionApiService,
} from './organization-session-api.service';

export interface AuthState {
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  userData: SignInUserData | null;
  backendIsOnline: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly ACCESS_TOKEN_KEY = 'accessToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private readonly ACCOUNT_ID_KEY = 'accountId';
  private readonly GROUP_KEY = 'group';
  private readonly USER_DATA_KEY = 'userData';
  private readonly USER_SETTINGS_KEY = 'userSettings';
  private readonly BACKEND_IS_ONLINE_KEY = 'backendIsOnline';
  private readonly SIGN_OUT_REASON_KEY = 'signOutReason';
  private readonly ORGANIZATIONS_KEY = 'organizations';

  public authState = signal<AuthState>({
    isAuthenticated: false,
    accessToken: null,
    refreshToken: null,
    userData: null,
    backendIsOnline: false,
  });

  constructor(
    private router: Router,
    private http: HttpClient,
    private storageService: StorageService,
    private platformService: PlatformService,
    private crossTabCommunication: CrossTabCommunicationService
  ) {
    this.initializeAuthState();
    this.setupCrossTabListeners();
  }

  private readonly authenticationApiService = inject(AuthenticationApiService);
  private readonly organizationSessionApiService = inject(OrganizationSessionApiService);

  /**
   * Initialize auth state from storage
   */
  private initializeAuthState(): void {
    if (!this.platformService.isBrowser()) {
      return;
    }

    const accessToken = this.storageService.getItem(this.ACCESS_TOKEN_KEY);
    const refreshToken = this.storageService.getItem(this.REFRESH_TOKEN_KEY);
    const userDataStr = this.storageService.getItem(this.USER_DATA_KEY);
    const userSettingsStr = this.storageService.getItem(this.USER_SETTINGS_KEY);
    const accountIdStr = this.storageService.getItem(this.ACCOUNT_ID_KEY);
    const groupStr = this.storageService.getItem(this.GROUP_KEY);
    const backendIsOnlineStr = this.storageService.getItem(this.BACKEND_IS_ONLINE_KEY);
    const passwordChangeRequiredStr = this.storageService.getItem('passwordChangeRequired');
    const backendIsOnline = backendIsOnlineStr === 'true';
    const passwordChangeRequired = passwordChangeRequiredStr === 'true';

    if (accessToken) {
      // Reconstruct SignInUserData from stored pieces
      let userData: SignInUserData | null = null;

      if (userDataStr && userSettingsStr && accountIdStr && groupStr) {
        try {
          const parsedUserData = JSON.parse(userDataStr);
          const parsedUserSettings = JSON.parse(userSettingsStr);
          const group = groupStr as any; // UserGroup enum

          // Reconstruct SignInUserData object
          userData = {
            accountId: accountIdStr,
            status: UserStatus.active, // Default to active, could be stored separately if needed
            group: group as UserGroup,
            passwordChangeRequired,
            accessToken,
            refreshToken: refreshToken || '',
            userData: parsedUserData,
            userSettings: parsedUserSettings,
          };
        } catch (error) {
          console.error('Error parsing stored user data:', error);
        }
      }

      // Check if token is valid
      if (this.isValid(accessToken)) {
        this.authState.set({
          isAuthenticated: true,
          accessToken,
          refreshToken,
          userData,
          backendIsOnline,
        });
        this.loadOrganizationsOnSessionInit();
      } else {
        // Token expired, try to refresh
        if (refreshToken) {
          this.refreshAccessToken().catch(() => {
            console.log('auth.service.ts:110 - Token expired and refresh failed');
            // this.signOut('auth.service.ts:110 - Token expired and refresh failed', this.router.url);
          });
        } else {
          console.log('auth.service.ts:113 - Token expired and no refresh token available');
          // this.signOut('auth.service.ts:113 - Token expired and no refresh token available', this.router.url);
        }
      }
    }
  }

  /**
   * Check if backend is available by calling health-check endpoint
   * @returns Promise<boolean> - true if backend is online, false otherwise
   */
  public async checkBackendHealth(): Promise<boolean> {
    if (!this.platformService.isBrowser()) {
      return false;
    }

    try {
      const url = `${environment.host}:${environment.port}/health`;
      const response = await firstValueFrom(
        this.http.get(url, { observe: 'response', responseType: 'text' }).pipe(
          catchError(() => {
            // Return a failed response indicator
            return of({ status: 0 } as { status: number });
          })
        )
      );

      // Health-check endpoint returns 204 (No Content) on success
      const isOnline = response && response.status === 204;
      this.updateBackendStatus(isOnline);
      return isOnline;
    } catch (error) {
      // Backend is offline
      const isOnline = false;
      this.updateBackendStatus(isOnline);
      return isOnline;
    }
  }

  /**
   * Update backend status in auth state and storage
   */
  private updateBackendStatus(isOnline: boolean): void {
    const currentState = this.authState();
    this.storageService.setItem(this.BACKEND_IS_ONLINE_KEY, isOnline.toString());
    this.authState.set({
      ...currentState,
      backendIsOnline: isOnline,
    });
  }

  /**
   * Setup cross-tab communication listeners
   */
  private setupCrossTabListeners(): void {
    this.crossTabCommunication.onSignOut().subscribe(() => {
      // Only process if we're still authenticated (avoid infinite loop)
      if (this.isAuthenticated()) {
        console.log('auth.service.ts:170 - Sign out triggered from another tab');
        // this.signOut('auth.service.ts:170 - Sign out triggered from another tab', this.router.url);
      }
    });

    this.crossTabCommunication.onAuthStateChange().subscribe(message => {
      // Only process if the message indicates we should clear auth
      // and we're currently authenticated (avoid infinite loop)
      if (!message.data?.['isAuthenticated'] && this.isAuthenticated()) {
        console.log('auth.service.ts:180 - Auth state changed in another tab');
        // this.signOut('auth.service.ts:180 - Auth state changed in another tab', this.router.url);
      }
    });
  }

  /**
   * Get bearer token, refreshing if needed
   * Based on the old authentication.service.ts getBearer() method
   * @returns Promise with Authorization header or null
   */
  public async getBearer(): Promise<{ Authorization: string } | null> {
    const accessToken = this.storageService.getItem(this.ACCESS_TOKEN_KEY);

    if (this.isValid(accessToken)) {
      return {
        Authorization: `Bearer ${accessToken}`,
      };
    } else {
      // Token is invalid or expired, try to refresh
      const refreshToken = this.storageService.getItem(this.REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        return null;
      }

      try {
        const newAccessToken = await this.refreshAccessToken();
        if (newAccessToken) {
          return {
            Authorization: `Bearer ${newAccessToken}`,
          };
        }
      } catch (error) {
        // Refresh failed, clear auth
        console.log('auth.service.ts:213 - Token refresh failed in getBearer');
        // this.signOut('auth.service.ts:213 - Token refresh failed in getBearer', this.router.url);
      }
      return null;
    }
  }

  /**
   * Refresh access token using the backend endpoint
   * Based on the old authentication.service.ts refresh token logic
   * Updates the token in localStorage and auth state
   * Does NOT navigate to sign-in on failure - let the interceptor handle that
   *
   * The old service pattern:
   * - Sends { refreshToken: string } to /api/authentication/refresh_token/
   * - On 200: if response is string, saves it and returns bearer
   * - On 401: clears all localStorage items and navigates to sign-in
   *
   * @returns Promise<string | null> - new access token or null if refresh failed
   */
  public async refreshAccessToken(): Promise<string | null> {
    const refreshToken = this.storageService.getItem(this.REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      return null;
    }

    try {
      // Use AuthenticationApiService.refreshToken() method instead of direct HTTP call
      const response = await firstValueFrom(
        this.authenticationApiService.refreshToken(refreshToken).pipe(
          catchError(error => {
            // Handle 401 errors specifically (like the old service)
            if (error.status === 401) {
              // Clear all auth data on 401 (matching old service behavior)
              // this.storageService.removeItem(this.ACCESS_TOKEN_KEY);
              // this.storageService.removeItem(this.REFRESH_TOKEN_KEY);
              // this.storageService.removeItem(this.ACCOUNT_ID_KEY);
              // this.storageService.removeItem(this.GROUP_KEY);
              // this.storageService.removeItem(this.USER_DATA_KEY);
              // this.storageService.removeItem(this.USER_SETTINGS_KEY);
            }
            // Return null on error - let the interceptor handle navigation
            return of(null);
          })
        )
      );

      if (response && typeof response === 'string') {
        // Update access token in storage and state (matching old service behavior)
        this.storageService.setItem(this.ACCESS_TOKEN_KEY, response);
        const currentState = this.authState();
        this.authState.set({
          ...currentState,
          accessToken: response,
        });
        return response;
      } else {
        // Invalid response - clear tokens (matching old service behavior)
        // this.storageService.removeItem(this.ACCESS_TOKEN_KEY);
        // this.storageService.removeItem(this.REFRESH_TOKEN_KEY);
        return null;
      }
    } catch (error) {
      // Return null on error - let the interceptor handle navigation
      return null;
    }
  }

  /**
   * Check if token is valid by decoding JWT and checking expiration
   * Based on the old authentication.service.ts isValid() method
   */
  private isValid(token: string | null): boolean {
    if (!token) {
      return false;
    }

    try {
      const jsonPayload = decodeURIComponent(
        atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      const payload = JSON.parse(jsonPayload);
      const exp = payload['exp'];
      return exp > Math.floor(Date.now() / 1000);
    } catch (error) {
      return false;
    }
  }

  /**
   * Set authentication data
   */
  public setAuthData(userData: SignInUserData): void {
    this.storageService.setItem(this.ACCESS_TOKEN_KEY, userData.accessToken);
    this.storageService.setItem(this.REFRESH_TOKEN_KEY, userData.refreshToken);
    this.storageService.setItem(this.ACCOUNT_ID_KEY, String(userData.accountId));
    this.storageService.setItem(this.GROUP_KEY, String(userData.group));
    this.storageService.setItem(this.USER_DATA_KEY, JSON.stringify(userData.userData));
    this.storageService.setItem(this.USER_SETTINGS_KEY, JSON.stringify(userData.userSettings));
    this.storageService.setItem('passwordChangeRequired', String(userData.passwordChangeRequired === true));

    this.authState.set({
      isAuthenticated: true,
      accessToken: userData.accessToken,
      refreshToken: userData.refreshToken,
      userData,
      backendIsOnline: true,
    });

    // Broadcast auth state change to other tabs
    this.crossTabCommunication.broadcastAuthStateChange(true, undefined);
    this.loadOrganizationsOnSessionInit();
  }

  /** Clear passwordChangeRequired flag after user completes force-password-change */
  public clearPasswordChangeRequired(): void {
    this.storageService.setItem('passwordChangeRequired', 'false');
    const currentState = this.authState();
    if (currentState.userData) {
      this.authState.set({
        ...currentState,
        userData: { ...currentState.userData, passwordChangeRequired: false },
      });
    }
  }

  public loadOrganizationsOnSessionInit(): void {
    if (!this.platformService.isBrowser() || !this.isAuthenticated()) {
      return;
    }

    this.organizationSessionApiService.getAllowedOrganizations().subscribe({
      next: organizations => {
        const normalized = (organizations || []).map(organization => ({
          id: String(organization.id), // Convert to string for UUID compatibility
          name: organization.name,
          description: organization.description ?? null,
          media: (organization.media || []).map(item => ({
            id: item.id,
            url: item.url,
            mime: item.mime || '',
            type: item.type,
          })),
        }));
        this.storageService.setItem(this.ORGANIZATIONS_KEY, JSON.stringify(normalized));
        window.dispatchEvent(new CustomEvent('organizationsUpdated'));
      },
      error: () => {
        this.storageService.setItem(this.ORGANIZATIONS_KEY, JSON.stringify([]));
        window.dispatchEvent(new CustomEvent('organizationsUpdated'));
      },
    });
  }

  public getAllowedOrganizationsFromStorage(): AllowedOrganization[] {
    if (!this.platformService.isBrowser()) {
      return [];
    }

    try {
      const raw = this.storageService.getItem(this.ORGANIZATIONS_KEY);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw) as AllowedOrganization[];
      return parsed.map(organization => ({
        id: String(organization.id), // Keep as string for UUID compatibility
        name: String(organization.name),
        description: organization.description ?? null,
        media: (organization.media || []).map(item => ({
          id: Number(item.id),
          url: String(item.url),
          mime: String(item.mime || ''),
          type: item.type,
        })),
      }));
    } catch {
      return [];
    }
  }

  public switchOrganization(organizationId: string): void {
    if (!this.platformService.isBrowser() || !this.isAuthenticated()) {
      return;
    }

    this.organizationSessionApiService.switchOrganization(organizationId).subscribe({
      next: response => {
        if (!response.accessToken || !response.refreshToken) {
          console.log('auth.service.ts:402 - switchOrganization returned empty tokens');
          // this.signOut('auth.service.ts:402 - switchOrganization returned empty tokens', this.router.url);
          return;
        }

        this.storageService.setItem(this.ACCESS_TOKEN_KEY, response.accessToken);
        this.storageService.setItem(this.REFRESH_TOKEN_KEY, response.refreshToken);
        const tokenPayload = this.decodeAccessTokenPayload(response.accessToken);
        if (tokenPayload?.group) {
          this.storageService.setItem(this.GROUP_KEY, String(tokenPayload.group));
        }

        const currentState = this.authState();
        const updatedUserData = currentState.userData
          ? {
              ...currentState.userData,
              accessToken: response.accessToken,
              refreshToken: response.refreshToken,
              group: (tokenPayload?.group as UserGroup) ?? currentState.userData.group,
            }
          : null;
        this.authState.set({
          ...currentState,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          userData: updatedUserData,
        });

        this.crossTabCommunication.broadcastAuthStateChange(true, undefined);
        this.loadOrganizationsOnSessionInit();

        const event = new CustomEvent('organizationsUpdated');
        window.dispatchEvent(event);
        window.location.reload();
      },
      error: () => {
        console.log('auth.service.ts:438 - switchOrganization failed');
        // this.signOut('auth.service.ts:438 - switchOrganization failed', this.router.url);
      },
    });
  }

  /**
   * Sign out user and clear authentication data
   * @param reason - Required reason for sign out (e.g., file and line where sign out was triggered)
   * @param returnUrl - Required return URL to redirect to after sign-in
   */
  public signOut(reason: string, returnUrl: string): void {
    this.storageService.removeItem(this.ACCESS_TOKEN_KEY);
    this.storageService.removeItem(this.REFRESH_TOKEN_KEY);
    this.storageService.removeItem(this.ACCOUNT_ID_KEY);
    this.storageService.removeItem(this.GROUP_KEY);
    this.storageService.removeItem(this.USER_DATA_KEY);
    this.storageService.removeItem(this.USER_SETTINGS_KEY);
    this.storageService.removeItem(this.BACKEND_IS_ONLINE_KEY);
    this.storageService.removeItem(this.ORGANIZATIONS_KEY);

    // Persist sign-out reason
    this.storageService.setItem(this.SIGN_OUT_REASON_KEY, reason);

    this.authState.set({
      isAuthenticated: false,
      accessToken: null,
      refreshToken: null,
      userData: null,
      backendIsOnline: false,
    });

    // Broadcast sign out to other tabs
    this.crossTabCommunication.broadcastSignOut();

    // Navigate to sign-out page with reason and returnUrl
    this.router.navigate(['/authentication/sign-out'], {
      queryParams: { returnUrl, reason },
    });
  }

  /**
   * Get the sign-out reason from storage
   * @returns The sign-out reason string or null if not set
   */
  public getSignOutReason(): string | null {
    return this.storageService.getItem(this.SIGN_OUT_REASON_KEY);
  }

  /**
   * Clear the sign-out reason from storage
   */
  public clearSignOutReason(): void {
    this.storageService.removeItem(this.SIGN_OUT_REASON_KEY);
  }

  /**
   * Get access token
   */
  public getAccessToken(): string | null {
    return this.authState().accessToken || this.storageService.getItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    return this.authState().isAuthenticated;
  }

  /**
   * Get user data
   */
  public getUserData(): SignInUserData | null {
    return this.authState().userData;
  }

  public getOrganizationIdFromAccessToken(): string | null {
    const payload = this.decodeAccessTokenPayload(this.getAccessToken());
    if (!payload?.organization_id) {
      return null;
    }
    // Convert to string (handles both UUID strings and legacy number IDs)
    return String(payload.organization_id);
  }

  private decodeAccessTokenPayload(
    token: string | null
  ): { organization_id?: string | number; group?: string } | null {
    if (!token) {
      return null;
    }

    try {
      const payloadPart = token.split('.')[1];
      if (!payloadPart) {
        return null;
      }
      const jsonPayload = decodeURIComponent(
        atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload) as { organization_id?: string | number; group?: string };
    } catch {
      return null;
    }
  }
}
